import { Grid } from '@mui/material'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { EventDto } from '../../../interfaces/IEvent'
import { EventType, PlannedWorkCategory, PlannedWorkCriticality, PlannedWorkStatus } from '../../../interfaces/IEventEnums'
import InputField from '../../../components/InputFields/InputField'
import AutocompleteField from '../../../components/InputFields/AutocompleteField'
import DateTimePickerField from '../../../components/InputFields/DateTimePickerField'
import { FieldName } from '../../../interfaces/IFields'
import MonoInputField from '../../../components/InputFields/MonoInputField'
import HourMinuteInputFields from '../../../components/InputFields/HourMinuteInputFields'

interface Props {
  onUpdate: (updatedEvent: EventDto) => void
  preDefinedFromDate?: Date
}

function CreatePlannedWorkEventGrid({ onUpdate, preDefinedFromDate }: Props) {
  const [plannedWorkEvent, setPlannedWorkEvent] = useState({
    type: EventType.Planlagt,
    eventDetails: {
      title: '',
      status: PlannedWorkStatus.Forespurt,
      fromDate: preDefinedFromDate ?? new Date(),
      toDate: null,
      description: '',
      estimatedDowntime: '',
      externalReference: '',
      category: PlannedWorkCategory.Standard,
      criticality: PlannedWorkCriticality.Low,
      responsible: {},
    },
  } as EventDto)

  const handleSelectChange = useCallback((name: string, value: string | Date | null) => {
    setPlannedWorkEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        [name]: value,
      },
    }))
  }, [])

  const handleStartDateChange = useCallback((date: Date | null) => {
    if (!date) return
    setPlannedWorkEvent((prevState) => {
      const eventToDate = prevState.eventDetails.toDate ? new Date(prevState.eventDetails.toDate) : null
      const toDate = eventToDate && date > eventToDate ? date : prevState.eventDetails.toDate
      return {
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          fromDate: date,
          toDate,
        },
      }
    })
  }, [])

  const handleEndDateChange = useCallback((date: Date | null) => {
    setPlannedWorkEvent((prevState) => {
      const eventFromDate = new Date(prevState.eventDetails.fromDate)
      if (date && eventFromDate > date) {
        return {
          ...prevState,
          eventDetails: {
            ...prevState.eventDetails,
            toDate: eventFromDate,
          },
        }
      }
      return {
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          toDate: date,
        },
      }
    })
  }, [])

  const handleEstimatedDowntimeChange = useCallback((newValue: string) => {
    setPlannedWorkEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        estimatedDowntime: newValue,
      },
    }))
  }, [])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (name === 'name' || name === 'id') {
      setPlannedWorkEvent((prevState) => ({
        ...prevState,
        eventDetails: {
          ...prevState.eventDetails,
          responsible: {
            ...prevState.eventDetails.responsible,
            [name]: value,
          },
        },
      }))
      return
    }

    setPlannedWorkEvent((prevState) => ({
      ...prevState,
      eventDetails: {
        ...prevState.eventDetails,
        [name]: value,
        responsible: {
          ...prevState.eventDetails.responsible,
        },
      },
    }))
  }, [])

  useEffect(() => {
    onUpdate(plannedWorkEvent)
  }, [plannedWorkEvent])

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 7,
        }}
      >
        <InputField
          name={FieldName.Title}
          title="Navn på hendelse"
          size="medium"
          value={plannedWorkEvent.eventDetails.title}
          required={true}
          onChange={handleInputChange}
          maxLength={80}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 5,
        }}
      >
        <InputField
          name={FieldName.Name}
          title="Ansvarlig"
          size="medium"
          placeholder="Ansvarlig for retting"
          value={plannedWorkEvent.eventDetails.responsible.name}
          onChange={handleInputChange}
          multiline={true}
          maxLength={30}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <AutocompleteField<PlannedWorkStatus>
          name={FieldName.Status}
          title="Status"
          placeholder="Ingen valgt"
          options={Object.values(PlannedWorkStatus)}
          onChange={(value: PlannedWorkStatus | null) => handleSelectChange(FieldName.Status, value)}
          getOptionLabel={(status) => status}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 7,
          md: 3,
        }}
      >
        <AutocompleteField<PlannedWorkCategory>
          name={FieldName.Category}
          title="Kategori"
          placeholder="Ingen valgt"
          options={Object.values(PlannedWorkCategory)}
          onChange={(value: PlannedWorkCategory | null) => handleSelectChange(FieldName.Category, value)}
          getOptionLabel={(category) => category}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 5,
          md: 3,
        }}
      >
        <AutocompleteField<PlannedWorkCriticality>
          name={FieldName.Criticality}
          title="Alvorlighetsgrad"
          placeholder="Ingen valgt"
          options={Object.values(PlannedWorkCriticality)}
          onChange={(value: PlannedWorkCriticality | null) => handleSelectChange(FieldName.Criticality, value)}
          getOptionLabel={(criticality) => criticality}
          isOptionEqualToValue={(option, value) => option === value}
          required={true}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <DateTimePickerField
          name={FieldName.FromDate}
          title="Startdato"
          required={true}
          value={plannedWorkEvent.eventDetails.fromDate}
          onChange={handleStartDateChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <DateTimePickerField
          name={FieldName.ToDate}
          title="Sluttdato"
          disablePast={true}
          minDateTime={plannedWorkEvent.eventDetails.fromDate}
          value={plannedWorkEvent.eventDetails.toDate}
          onChange={handleEndDateChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <HourMinuteInputFields
          name={FieldName.EstimatedDowntime}
          size="medium"
          title="Estimert nedetid"
          onChange={handleEstimatedDowntimeChange}
          value={plannedWorkEvent.eventDetails.estimatedDowntime}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 12,
        }}
      >
        <InputField
          name={FieldName.ExternalReference}
          title="Ekstern referanse"
          size="medium"
          value={plannedWorkEvent.eventDetails.externalReference}
          onChange={handleInputChange}
          multiline={true}
          maxLength={25}
        />
      </Grid>
      <Grid size={12}>
        <MonoInputField
          name={FieldName.Description}
          title="Beskrivelse"
          size="medium"
          value={plannedWorkEvent.eventDetails.description}
          onChange={handleInputChange}
          multiline={true}
          rows={6}
          maxLength={1000}
        />
      </Grid>
    </>
  )
}

export default CreatePlannedWorkEventGrid
